// Python 3.11 (Unicode 14) text semantics, independent of the browser's own
// Unicode tables (Codex W1 r1 B3). The API builds deck-box keys under CPython
// 3.11 (mtg-api Dockerfile `FROM python:3.11-slim`); a browser ships newer
// case data (U+A7CB lowercases to U+0264 in JavaScript, not in Python 3.11),
// so nothing here falls back to toLowerCase() or to the runtime's property
// classes.
//
//   pyLower       str.lower(), Final_Sigma included
//   pyCasefold    str.casefold()
//   pyNormalize   unicodedata.normalize() for NFKC / NFKD
//   PY_SPACE_CLASS / PY_DECIMAL_CLASS
//                 regex classes for str.isspace() (re `\s`) and
//                 str.isdecimal() (re `\d`); use with the `u` flag.
//
// Normalization is the one step that still uses the runtime (pinning the
// decomposition and composition data would be another ~100 KB), guarded so it
// cannot drift: Unicode's normalization stability policy freezes the result
// for every code point already assigned in Unicode 14, so only the runtime's
// treatment of LATER code points can differ. Python 3.11 sees those as
// unassigned (no decomposition, combining class 0, which also blocks
// composition across them), so they are split out and passed through
// untouched, exactly as Python does, and only the Unicode-14 runs are
// normalized by the runtime. The exhaustive parity test checks every code
// point against digests computed by CPython 3.11.
//
// Tables: src/lib/pyUnicodeTables.generated.ts, written by
// scripts/gen-py-unicode-tables.py (run it with Python 3.11; regenerate only
// if the API's Python moves).
import {
  ASSIGNED, CASED, CASE_IGNORABLE, CASEFOLD_MULTI, CASEFOLD_RUNS, DECIMAL, LOWER_MULTI, LOWER_RUNS, WHITESPACE,
} from './pyUnicodeTables.generated'

type Ranges = readonly (readonly number[])[]

function buildMap(runs: Ranges, multi: Ranges): Map<number, string> {
  const m = new Map<number, string>()
  for (const [start, count, step, delta] of runs) {
    for (let i = 0; i < count; i++) {
      const cp = start + i * step
      m.set(cp, String.fromCodePoint(cp + delta))
    }
  }
  for (const [cp, ...targets] of multi) m.set(cp, String.fromCodePoint(...targets))
  return m
}

let lowerMap: Map<number, string> | null = null
let foldMap: Map<number, string> | null = null

function inRanges(ranges: Ranges, cp: number): boolean {
  let lo = 0
  let hi = ranges.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const [a, b] = ranges[mid]
    if (cp < a) hi = mid - 1
    else if (cp > b) lo = mid + 1
    else return true
  }
  return false
}

const CAPITAL_SIGMA = 0x3a3
const SMALL_SIGMA = 'σ'
const FINAL_SIGMA = 'ς'

/** CPython's handle_capital_sigma: \p{cased}\p{case-ignorable}* SIGMA !(\p{case-ignorable}* \p{cased}). */
function sigmaAt(cps: number[], i: number): string {
  let j = i - 1
  while (j >= 0 && inRanges(CASE_IGNORABLE, cps[j])) j--
  if (j < 0 || !inRanges(CASED, cps[j])) return SMALL_SIGMA
  let k = i + 1
  while (k < cps.length && inRanges(CASE_IGNORABLE, cps[k])) k++
  return k === cps.length || !inRanges(CASED, cps[k]) ? FINAL_SIGMA : SMALL_SIGMA
}

/** Python 3.11 str.lower(). */
export function pyLower(s: string): string {
  lowerMap ??= buildMap(LOWER_RUNS, LOWER_MULTI)
  const cps = Array.from(s, ch => ch.codePointAt(0)!)
  let out = ''
  for (let i = 0; i < cps.length; i++) {
    const cp = cps[i]
    out += cp === CAPITAL_SIGMA ? sigmaAt(cps, i) : (lowerMap.get(cp) ?? String.fromCodePoint(cp))
  }
  return out
}

/** Python 3.11 str.casefold() (no context, so a final capital sigma folds to σ). */
export function pyCasefold(s: string): string {
  foldMap ??= buildMap(CASEFOLD_RUNS, CASEFOLD_MULTI)
  let out = ''
  for (const ch of s) out += foldMap.get(ch.codePointAt(0)!) ?? ch
  return out
}

/** Assigned in Unicode 14 (Python 3.11's unicodedata), surrogates included as themselves. */
export function isAssignedInPy(cp: number): boolean {
  return inRanges(ASSIGNED, cp)
}

/**
 * Python 3.11 unicodedata.normalize(form, s): the runtime normalizes each run
 * of Unicode-14 code points; any later code point is left exactly as is (see
 * the module comment).
 */
export function pyNormalize(s: string, form: 'NFKC' | 'NFKD'): string {
  let out = ''
  let run = ''
  for (const ch of s) {
    if (isAssignedInPy(ch.codePointAt(0)!)) {
      run += ch
    } else {
      if (run) out += run.normalize(form)
      run = ''
      out += ch
    }
  }
  return run ? out + run.normalize(form) : out
}

function rangeClass(ranges: Ranges): string {
  // eslint-disable-next-line i18next/no-literal-string -- a regex escape, never rendered
  const hex = (cp: number) => `\\u{${cp.toString(16)}}`
  return ranges.map(([a, b]) => (a === b ? hex(a) : `${hex(a)}-${hex(b)}`)).join('')
}

/** str.isspace() (Python re `\s` in a str pattern) as a character-class body; needs the `u` flag. */
export const PY_SPACE_CLASS = rangeClass(WHITESPACE)

/** str.isdecimal() (Python re `\d` in a str pattern) as a character-class body; needs the `u` flag. */
export const PY_DECIMAL_CLASS = rangeClass(DECIMAL)
